import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type QueryClient from "../../api/query/queryClient";
import { practiceSelectionKey } from "../../utils/utils";
import Loading from "../Loading";
import PageNotFound from "../PageNotFound";

const METABASE_SITE_URL = "https://wieldy.metabaseapp.com";
const PRACTICE_NOT_SELECTED = "PRACTICE_NOT_SELECTED";

interface MetabaseViewProps {
  queryClient: QueryClient;
  selectedPractices: PracticeMessage[];
  organization: OrganizationMessage | null;
}

export default function MetabaseView({
  queryClient,
  selectedPractices,
  organization,
}: MetabaseViewProps) {
  const selectedPracticesKey = practiceSelectionKey(selectedPractices);
  const [iframeUrl, setIframeUrl] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchParams] = useSearchParams();

  const updateTabName = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "adjustments" && organization?.metabaseAdjustmentTabName) {
      return `?tab=${organization.metabaseAdjustmentTabName}`;
    }
    return "";
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding organization, selectedPractices.length, updateTabName and 2 more or dropping selectedPracticesKey, searchParams would change when this runs
  useEffect(() => {
    if (selectedPractices.length === 0) {
      setIsLoading(false);
      setIframeUrl(PRACTICE_NOT_SELECTED);
      return;
    }
    const practiceNames = selectedPractices.map((p) => p.displayName);

    queryClient
      .getMetabaseToken(practiceNames)
      .then((res) => {
        const tabSuffix = organization ? updateTabName() : "";
        const url =
          `${METABASE_SITE_URL}/embed/dashboard/${res.token}` +
          `${tabSuffix}` +
          `#background=false&bordered=false&titled=false`;
        setIframeUrl(url);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedPracticesKey, searchParams]);

  if (isLoading) {
    return (
      <div style={{ display: "flex", height: "80vh" }}>
        <Loading />
      </div>
    );
  }

  if (!iframeUrl) {
    return <PageNotFound />;
  }

  if (iframeUrl === PRACTICE_NOT_SELECTED) {
    return (
      <PageNotFound message="Please select at least one practice to view reports" />
    );
  }

  return (
    <>
      <h1>Reports</h1>
      <iframe
        title="Metabase"
        src={iframeUrl}
        style={{
          width: "100%",
          height: "100vh",
          border: "0px",
        }}
        // biome-ignore lint/suspicious/noUnknownAttribute: legacy iframe attribute for the Metabase embed
        allowTransparency
      />
    </>
  );
}
